import * as THREE from 'three'
import type { ModelBodySceneRegistry } from '../../model-body/ModelBodySceneRegistry'
import { resolveModelBodySceneRegistry } from '../../model-body/model-body-scene-registry-runtime'
import {
  collectImportedModelTopologyRepositories,
  collectImportedModelTopologyRepositorySceneAnchors,
} from '../topology/ImportedModelTopologyRepository'

/** 每个统一实体注册表最近一次由导入边界登记的表示键。 */
const importedRepresentationIdsByRegistry = new WeakMap<ModelBodySceneRegistry, Set<string>>()

/**
 * 规范化可选精确别名。
 *
 * @param values 候选 ID。
 * @returns 去重后的非空精确别名。
 */
function normalizeAliasIds(values: readonly unknown[]): string[] {
  const aliasIds = new Set<string>()
  values.forEach((value) => {
    if (typeof value !== 'string') {
      return
    }
    const normalizedValue = value.trim()
    if (normalizedValue) {
      aliasIds.add(normalizedValue)
    }
  })
  return [...aliasIds]
}

/**
 * 将 imported topology repository 中的实体表示接入统一场景实体注册表。
 *
 * 边界约束：
 * 1. 本函数是导入数据结构唯一允许出现的适配入口；
 * 2. geometryObjectId 一旦进入注册表就同时作为初始 modelBodyId 与 modelBodyRevisionId；
 * 3. 后续预览、特征提交、Undo/Redo 和拓扑 owner 解析不再识别 bodyBindingId 或 imported 类型。
 *
 * @param scene 当前 Viewer 场景。
 * @param registry 可选的场景统一实体注册表。
 * @param options 导入边界增量同步选项。
 * @returns 本次登记的导入实体数量。
 */
export function syncImportedModelBodyRepresentations(
  scene: THREE.Object3D,
  registry: ModelBodySceneRegistry = resolveModelBodySceneRegistry(scene),
  options: {
    /** 可选的本次新增子树；不含导入拓扑仓库时直接跳过全场景扫描。 */
    changedSubtree?: THREE.Object3D
  } = {},
): number {
  if (
    options.changedSubtree
    && collectImportedModelTopologyRepositories(options.changedSubtree).size === 0
  ) {
    return 0
  }
  const nextRepresentationIds = new Set<string>()

  collectImportedModelTopologyRepositorySceneAnchors(scene).forEach((sceneScopeAnchor, repository) => {
    repository.listBodyRecords().forEach((bodyRecord) => {
      const geometryObjectId = repository.resolveGeometryObjectIdForBodyRecord(bodyRecord).trim()
      if (!geometryObjectId) {
        throw new Error(`导入实体缺少统一 geometryObjectId: ${bodyRecord.bodyBindingId}`)
      }
      const representationId = [
        geometryObjectId,
        bodyRecord.visibleMesh.uuid,
        bodyRecord.bodyBindingId,
      ].join('::representation::')
      const topologyMetadataObjectId = bodyRecord.topologyAsset.meshData.metadata?.objectId
      const currentPickHost = bodyRecord.getPickHost()
      nextRepresentationIds.add(representationId)

      registry.registerRepresentation({
        representationId,
        modelBodyId: geometryObjectId,
        modelBodyRevisionId: geometryObjectId,
        aliasIds: normalizeAliasIds([
          bodyRecord.bodyBindingId,
          bodyRecord.topologyAsset.geometryObjectId,
          bodyRecord.topologyAsset.meshData.id,
          topologyMetadataObjectId,
          currentPickHost?.objectId,
        ]),
        displayMesh: bodyRecord.visibleMesh,
        sceneScopeAnchor,
        topologyOwner: currentPickHost,
        resolveTopologyOwner: () => repository.ensurePickHost(bodyRecord),
        primitiveIndices: bodyRecord.primitiveIndices,
      })

      /**
       * 同步 visibleMesh 的 name 属性为 geometryObjectId，使 Viewer.findSceneObjectById
       * 能够通过 getObjectByName 回退路径查找到导入模型的场景对象。
       *
       * 设计说明：
       * 1. 原生模型（FeatureResultMesh）在构造时执行 `this.name = this.objectId`，
       *    导入模型缺少这一赋值，导致依赖 sceneObject 查找的功能（如预览气泡坐标投影）失败；
       * 2. 同一 visibleMesh 可能被多个 bodyRecord 共享（多实体单网格），多次赋值无副作用：
       *    `localToWorld` 对同一 mesh 使用同一世界矩阵，任一 geometryObjectId 均可正确投影；
       * 3. 由于 bodyRecord 遍历顺序稳定（listBodyRecords），多次调用本函数时 name 赋值亦稳定。
       */
      bodyRecord.visibleMesh.name = geometryObjectId
    })
  })

  const previousRepresentationIds = importedRepresentationIdsByRegistry.get(registry) ?? new Set<string>()
  previousRepresentationIds.forEach((representationId) => {
    if (!nextRepresentationIds.has(representationId)) {
      registry.unregisterRepresentation(representationId)
    }
  })
  importedRepresentationIdsByRegistry.set(registry, nextRepresentationIds)
  return nextRepresentationIds.size
}
